import React, { ElementType, PropsWithChildren, ReactElement, ReactNode, isValidElement } from 'react';

import classNames from 'classnames';
import { StaticImageData } from 'next/image';

import { Container } from '@/blocks/components/Container';
import { LinkSignup } from '@/blocks/components/PlatformLink';
import { UrgentPicture } from '@/blocks/components/UrgentPicture';
import { ClassName, GenericCtaProps, WithClassName } from '@/blocks/components/types';
import { EventBlockNames } from '@/providers/analytics/types';
import background from '@/styles/background.module.scss';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';
import { isStaticImageData } from '@/utils/image/isStaticImageData';

import styles from './styles.module.scss';

type HeroSectionProps = {
  className?: ClassName;
  rightSideLayout?: boolean;
  withHeaderSecondNavRow?: boolean;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actionsSlot?: ReactNode;
  body?: React.ReactNode;
  footer?: React.ReactNode;
  // deprecated
  // mainCta?: ReactNode;
  // secondaryCta?: {
  //   href: any;
  //   text: string;
  // };
  // subCta?: React.ReactNode;
  // subCtaClassName?: classNames.ArgumentArray | classNames.Argument;
  classes?: {
    title?: ClassName;
    subtitle?: ClassName;
    firstBlock?: ClassName;
    secondBlock?: ClassName;
    actionsSlot?: ClassName;
  };
};

export function HeroCta<T extends ElementType>({
  children,
  // @ts-ignore
  Component = LinkSignup,
  className,
  ...props
}: GenericCtaProps<T>) {
  const ComponentElement = Component as ElementType;
  return (
    <ComponentElement
      className={classNames(styles.ctaButton, buttons.btn, buttons.btnPrimary, className)}
      {...props}
    >
      {children}
    </ComponentElement>
  );
}

export function HeroBlock({
  className,
  rightSideLayout,
  title,
  subtitle,
  body,
  actionsSlot,
  footer,
  classes,
  ...rest
}: Omit<HeroSectionProps, 'withHeaderSecondNavRow'>) {
  return (
    <div className={classNames(rightSideLayout ? styles.heroLayoutRight : styles.heroLayout)} {...rest}>
      <div className={classNames(classes?.firstBlock || styles.firstBlock)}>
        <div className={classNames(styles.heroHeadingSlot)}>
          <h1 className={typography.h1}>{title}</h1>
          {subtitle && (
            <p className={classNames(classes?.subtitle || typography.subheadingML, styles.subtitle)}>
              {subtitle}
            </p>
          )}
        </div>
        {actionsSlot && (
          <div className={classNames(classes?.actionsSlot || styles.actionsSlot)}>{actionsSlot}</div>
        )}
      </div>
      {!body ? null : <div className={classNames(classes?.secondBlock || styles.secondBlock)}>{body}</div>}
      {!footer ? null : <div className={classNames(styles.heroFooterSlot)}>{footer}</div>}
    </div>
  );
}

export function HeroSection(props: HeroSectionProps) {
  const { withHeaderSecondNavRow = false, ...otherProps } = props;

  return (
    <section
      data-event-block-name={EventBlockNames.hero}
      className={classNames(
        background.geometryBgDark,
        styles.heroSection,
        {
          [styles.withHeaderSecondNavRow]: withHeaderSecondNavRow,
        },
        props.className,
      )}
    >
      <Container>
        <HeroBlock {...otherProps} />
      </Container>
    </section>
  );
}

export function HeroSectionWithInsertBefore(props: HeroSectionProps & PropsWithChildren) {
  const { withHeaderSecondNavRow = false, ...otherProps } = props;
  return (
    <section
      className={classNames(
        styles.heroSection,
        {
          [styles.withHeaderSecondNavRow]: withHeaderSecondNavRow,
        },
        props.className,
      )}
      data-event-block-name={EventBlockNames.hero}
    >
      {props.children}
      <Container>
        <HeroBlock {...otherProps} />
      </Container>
    </section>
  );
}

export type HeroSideImageProps = {
  image:
    | ReactElement
    | {
        src: StaticImageData;
        alt?: string;
        width?: number;
        height: number;
        blurredLoading?: boolean;
      };
  slot?: ReactNode;
} & WithClassName;

export const HeroSideImage = ({ image, slot, className }: HeroSideImageProps) => {
  if (isValidElement(image)) {
    return (
      <div className={classNames(styles.sideWrapper, className)}>
        <div className={styles.imageWrapper}>
          {image}
          {slot}
        </div>
      </div>
    );
  }

  const { width = 457, height, ...props } = image as unknown as StaticImageData;
  return (
    <div className={classNames(styles.sideWrapper, className)}>
      <div className={styles.imageWrapper}>
        <UrgentPicture sizes={'100vw'} alt={''} {...props} width={width} height={height} />
        {slot}
      </div>
    </div>
  );
};
